import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import GenericPopup from "@Components/Popup/Popup";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import AddButton from "@Components/Buttons/AddButton";
import ListingPageTemplate from "@Pages/ListingPageTemplate/ListingPageTemplate";
import themeCreator from "@Themes/themeCreator";
import useDeleteItem from "@Hooks/useDeleteItem";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import useSendPutRequest from "../../Hooks/useSendPutRequest";
import { EditSectionPopup } from "../GenericForm/EditSectionPopup";
import GenericFilterForEnums from "../../Components/GenericFillterForEnums/GenericFillterForEnums.js";
import { castOptionsToFitOptionsFilter } from "../../Utils/Validations/HelperFunctions/FormaterFunctions";
import StarIcon from "@mui/icons-material/Star";
import MeatballsMenu from "@Components/MeatballsMenu/MeatballsMenu";
import {
  DebitAccount,
  inputTypes,
  transactionStatus,
  TRANSACTION_EXPORT_COLUMN_OPTIONS,
  MaturityType,

} from "../../Utils/HelpingObjects/HelpingObjects";
import {
  SAVE_DOCUMENT,
  BANKS_URL,
  GET_STARRED_TRANSACTION_ORDERS,
  DELETE_ORDER,
  SUPPLIERS_URL,
  UPDATE_BATCH_TRANSACTION_ORDERS,
  UPDATE_TRANSACTION_ORDER,
  UPDATE_GROUP_TRANSACTION_ORDER,
  EXPORT_PAYMENT_XSLS,
  UNSTAR_ORDERS_URL,
  STAR_ORDERS_URL,
  GET_GROUPED_TRANSACTION_ORDERS,
  EXPORT_TRANSACTION_ORDERS_XSLS,
} from "@API/APIRoutes";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { ThemeProvider } from "@mui/system";
import usePostItem from "@Hooks/usePostItem";
import { useNavigate } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import axios from "axios";
import usePaginate from "@Hooks/usePaginate";
import useGetManyRequests from "@Hooks/useGetManyRequests";
import {
  getGridStringOperators,
  getGridNumericOperators,
} from "@mui/x-data-grid";
import axiosInstance from "@API/axios";
import { Snackbar, Alert } from "@mui/material";
import { encryptId } from "../../Utils/SecurityFunctions";
import EditIcon from "@mui/icons-material/Edit";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Currency } from "../../Utils/HelpingObjects/HelpingObjects";
import ExportSelectionPopover from "@Components/ExportSelectionPopover/ExportSelectionPopover";

const toDisplayString = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const joinValues = (values) =>
  [...new Set(values.map(toDisplayString).filter(Boolean))]
    .sort((first, second) =>
      first.localeCompare(second, undefined, { numeric: true, sensitivity: "base" })
    )
    .join(", ");

const formatDecimal = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  return Number.isFinite(number)
    ? String(Math.round((number + Number.EPSILON) * 1000) / 1000)
    : "";
};

const formatGroupDate = (date) => {
  if (!date) return "";
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleDateString("en-GB");
};

const formatGroupState = (state) =>
  state
    ? state.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "";

// Columns that have an editor in "Edit Current Page" mode.
const BATCH_EDITABLE_FIELDS = new Set([
  "transactionStatus",
  "supplier",
  "bank",
  "idcNumber",
  "invoiceAmount",
  "invoiceCurrency",
  "maturityDate",
  "paymentDate",
  "debitAccount",
  "exchangeRate",
]);

const GROUP_EDITABLE_FIELDS = [
  ["exchangeRate", ""],
  ["transactionComments", ""],
  ["debitAccount", ""],
  ["authorizationDate", ""],
  ["paymentDate", ""],
  ["submittedOnPortal", false],
];

// Seeds the group edit form: the shared value when all orders agree, otherwise empty.
const getGroupEditValues = (orders) =>
  GROUP_EDITABLE_FIELDS.reduce((acc, [key, emptyValue]) => {
    const first = orders[0]?.[key] ?? emptyValue;
    acc[key] = orders.every((order) => (order[key] ?? emptyValue) === first)
      ? first
      : emptyValue;
    return acc;
  }, {});

// Mirrors the backend status rules when a group is edited in bulk.
const deriveTransactionStatus = (edit, currentStatus) => {
  if (edit.authorizationDate) return "AUTHORIZED";
  if (edit.submittedOnPortal) return "SUBMITTED";
  if (edit.paymentDate) return "PAID";
  return currentStatus;
};

// Group rows are summaries of their orders; each column shows the
// distinct values of the group's orders (amounts are summed).
const buildGroupRow = (group) => {
  const orders = Array.isArray(group?.orders) ? group.orders : [];
  const amounts = orders
    .map((order) => order.invoiceAmount)
    .filter((amount) => amount !== null && amount !== undefined);
  const display = {
    poNumber: joinValues(orders.map((order) => order.poNumber)),
    transactionStatus: joinValues(
      orders.map((order) => formatGroupState(order.transactionStatus || "PENDING"))
    ),
    supplier: joinValues(
      orders.map((order) => order.supplierName || order.supplier?.supplierName)
    ),
    bank: joinValues(orders.map((order) => order.bankName || order.bank?.bankName)),
    idcNumber: joinValues(orders.map((order) => order.idcNumber)),
    invoiceAmount: amounts.length
      ? formatDecimal(amounts.reduce((sum, amount) => sum + Number(amount), 0))
      : "",
    invoiceCurrency: joinValues(orders.map((order) => order.invoiceCurrency)),
    maturityDate: joinValues(orders.map((order) => formatGroupDate(order.maturityDate))),
    paymentDate: joinValues(orders.map((order) => formatGroupDate(order.paymentDate))),
    submittedOnPortal: orders.length
      ? orders.every((order) => order.submittedOnPortal)
        ? "Yes"
        : orders.some((order) => order.submittedOnPortal)
        ? "Partially"
        : "No"
      : "",
    debitAccount: joinValues(orders.map((order) => order.debitAccount)),
    exchangeRate: joinValues(
      orders.map((order) => formatDecimal(order.exchangeRate))
    ),
    bolNumber: joinValues(orders.map((order) => order.bolNumber)),
    bolDate: joinValues(orders.map((order) => formatGroupDate(order.bolDate))),
    maturityType: joinValues(orders.map((order) => order.maturityType)),
    maturityPeriod: joinValues(orders.map((order) => order.maturityPeriod)),
    proceededManually: joinValues(
      orders.map((order) =>
        order.proceededManually === null || order.proceededManually === undefined
          ? ""
          : order.proceededManually
          ? "Yes"
          : "No"
      )
    ),
    coraId: joinValues(orders.map((order) => order.coraId)),
    debitValue: joinValues(
      orders.map((order) => formatDecimal(order.debitValue))
    ),
    transactionComments: joinValues(
      orders.map((order) => order.transactionComments)
    ),
    invoiceNumber: joinValues(orders.map((order) => order.invoiceNumber)),
    invoiceDate: joinValues(
      orders.map((order) => formatGroupDate(order.invoiceDate))
    ),
    authorizationDate: joinValues(
      orders.map((order) => formatGroupDate(order.authorizationDate))
    ),
    uploadOnPortalDate: joinValues(
      orders.map((order) => formatGroupDate(order.uploadOnPortalDate))
    ),
    form4SendingDate: joinValues(
      orders.map((order) => formatGroupDate(order.form4SendingDate))
    ),
    form4ReceivalDate: joinValues(
      orders.map((order) => formatGroupDate(order.form4ReceivalDate))
    ),
  };
  return {
    id: `group-${group.groupId}`,
    isGroup: true,
    groupId: group.groupId,
    orders,
    groupStatuses: [
      ...new Set(orders.map((order) => order.transactionStatus || "PENDING")),
    ],
    groupDisplay: display,
    editValues: getGroupEditValues(orders),
    poNumber: display.poNumber,
    supplierName: display.supplier,
    bankName: display.bank,
  };
};


function TreasuryCoordinatorOrderListing({
  isStarredPage = false,
  isEdit = true,
  title = "Transaction Listing",
}) {
  const theme = themeCreator();
  const error_message = `Failed to Fetch Invoices Trying Again!`;
  const [selectedId, setSelectedId] = useState(null);
  const [exportAnchorPosition, setExportAnchorPosition] = useState(null);
  const { isLoading: PutItemLoading, requestMethod: putItem } =
    useSendPutRequest(true);
  const { isLoading: batchPutLoading, requestMethod: batchPutItem } =
    useSendPutRequest(true);
  const { loading: primitiveLoading, getAll: getPrimitiveData } =
    useGetManyRequests();
  // Add these state declarations near your other useState declarations:
  const [isDownloading, setIsDownloading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const navigate = useNavigate();
  const closeExportPopup = () => setExportAnchorPosition(null);
  const openExportPopup = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setExportAnchorPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };
  const isExportPopoverOpen = Boolean(exportAnchorPosition);
  //********************* PAGINATION CONTROL *********************//
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const { loading: pageLoading, getPage } = usePaginate();
  const [showStarPopup, setShowStarPopup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isBatchEditMode, setIsBatchEditMode] = useState(false);
  const [editedRows, setEditedRows] = useState({});
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [bankOptions, setBankOptions] = useState([]);

  const { loading: unstarOrderLoading, postItem: unstarOrder } = usePostItem();
  const { loading: starOrderLoading, postItem: starOrder } = usePostItem();

  useEffect(() => {
    getPrimitiveData([SUPPLIERS_URL, BANKS_URL]).then((responses) => {
      const [suppliers, banks] = responses;
      setSupplierOptions(
        (suppliers || []).map((item) => ({
          id: String(item.supplierId),
          name: item.supplierName,
        }))
      );
      setBankOptions(
        (banks || []).map((item) => ({
          id: String(item.bankId),
          name: item.bankName,
        }))
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasUnsavedBatchEdits = Object.keys(editedRows).length > 0;
  const supplierNameById = useMemo(
    () =>
      supplierOptions.reduce((acc, option) => {
        acc[String(option.id)] = option.name;
        return acc;
      }, {}),
    [supplierOptions]
  );
  const bankNameById = useMemo(
    () =>
      bankOptions.reduce((acc, option) => {
        acc[String(option.id)] = option.name;
        return acc;
      }, {}),
    [bankOptions]
  );

  const normaliseDateValue = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return typeof value === "string" ? value.slice(0, 10) : "";
    }
    return parsed.toISOString().slice(0, 10);
  };

  const normaliseOrderForBatchEditing = (row) => ({
    transactionStatus: row.transactionStatus || "PENDING",
    supplierId:
      row.supplierId !== undefined && row.supplierId !== null
        ? String(row.supplierId)
        : row?.supplier?.supplierId !== undefined &&
          row?.supplier?.supplierId !== null
        ? String(row.supplier.supplierId)
        : "",
    bankId:
      row.bankId !== undefined && row.bankId !== null
        ? String(row.bankId)
        : row?.bank?.bankId !== undefined && row?.bank?.bankId !== null
        ? String(row.bank.bankId)
        : "",
    idcNumber: row.idcNumber ?? "",
    invoiceAmount: row.invoiceAmount ?? "",
    invoiceCurrency: row.invoiceCurrency ?? "",
    maturityDate: normaliseDateValue(row.maturityDate),
    paymentDate: normaliseDateValue(row.paymentDate),
    debitAccount: row.debitAccount ?? "",
    exchangeRate: row.exchangeRate ?? "",
  });

  // A group row starts from the value its orders share, or empty when they differ.
  const normaliseRowForBatchEditing = (row) => {
    if (!row.isGroup) {
      return normaliseOrderForBatchEditing(row);
    }
    const bases = row.orders.map(normaliseOrderForBatchEditing);
    const first = bases[0] || normaliseOrderForBatchEditing({});
    return Object.keys(first).reduce((acc, key) => {
      acc[key] = bases.every((base) => base[key] === first[key])
        ? first[key]
        : "";
      return acc;
    }, {});
  };

  const mergeRowWithDraft = (row) => ({
    ...normaliseRowForBatchEditing(row),
    ...(editedRows[row.id] || {}),
  });

  const getDraftValue = (row, field) => mergeRowWithDraft(row)[field];

  const updateEditedRow = (row, field, value) => {
    const baseRow = normaliseRowForBatchEditing(row);
    const nextDraft = {
      ...(editedRows[row.id] || {}),
      [field]: value,
    };

    const cleanedDraft = Object.entries(nextDraft).reduce((acc, [key, draftValue]) => {
      if (draftValue !== baseRow[key]) {
        acc[key] = draftValue;
      }
      return acc;
    }, {});

    setEditedRows((prev) => {
      if (Object.keys(cleanedDraft).length === 0) {
        const { [row.id]: removedRow, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [row.id]: cleanedDraft,
      };
    });
  };

  const discardBatchChanges = () => {
    setEditedRows({});
    setIsBatchEditMode(false);
  };

  const openBatchEditMode = () => {
    setEditedRows({});
    setIsBatchEditMode(true);
  };

  const batchSaveCurrentPage = async () => {
    if (!hasUnsavedBatchEdits) {
      setIsBatchEditMode(false);
      return;
    }

    // A group edit is expanded to one entry per order. Only the fields changed
    // on the group row are sent; null fields are left untouched by the backend.
    const buildGroupEntries = (row) => {
      const draft = editedRows[row.id];
      const has = (key) => draft[key] !== undefined;
      return row.orders.map((order) => ({
        orderId: Number(order.id),
        transactionStatus: has("transactionStatus")
          ? draft.transactionStatus
          : has("paymentDate")
          ? draft.paymentDate ? "PAID" : "PENDING"
          : null,
        supplierId: has("supplierId") && draft.supplierId ? Number(draft.supplierId) : null,
        bankId: has("bankId") && draft.bankId ? Number(draft.bankId) : null,
        idcNumber: has("idcNumber") ? draft.idcNumber : null,
        invoiceAmount:
          has("invoiceAmount") && draft.invoiceAmount !== ""
            ? Number(draft.invoiceAmount)
            : null,
        invoiceCurrency: has("invoiceCurrency") ? draft.invoiceCurrency || null : null,
        maturityDate: has("maturityDate") ? draft.maturityDate || null : null,
        paymentDate: has("paymentDate") ? draft.paymentDate || null : null,
        debitAccount: has("debitAccount") ? draft.debitAccount || null : null,
        exchangeRate:
          has("exchangeRate") && draft.exchangeRate !== ""
            ? Number(draft.exchangeRate)
            : null,
      }));
    };

    const payload = rows
      .filter((row) => editedRows[row.id])
      .flatMap((row) => {
        if (row.isGroup) {
          return buildGroupEntries(row);
        }
        const mergedRow = mergeRowWithDraft(row);
        const paymentDate = mergedRow.paymentDate || null;
        const transactionStatus = editedRows[row.id].transactionStatus
          ? mergedRow.transactionStatus
          : editedRows[row.id].paymentDate !== undefined
          ? paymentDate ? "PAID" : "PENDING"
          : mergedRow.transactionStatus;

        return {
          orderId: Number(row.id),
          transactionStatus,
          supplierId: mergedRow.supplierId ? Number(mergedRow.supplierId) : null,
          bankId: mergedRow.bankId ? Number(mergedRow.bankId) : null,
          idcNumber: mergedRow.idcNumber ?? "",
          invoiceAmount:
            mergedRow.invoiceAmount === "" || mergedRow.invoiceAmount === null
              ? null
              : Number(mergedRow.invoiceAmount),
          invoiceCurrency: mergedRow.invoiceCurrency || null,
          maturityDate: mergedRow.maturityDate || null,
          paymentDate,
          debitAccount: mergedRow.debitAccount || null,
          exchangeRate:
            mergedRow.exchangeRate === "" || mergedRow.exchangeRate === null
              ? null
              : Number(mergedRow.exchangeRate),
        };
      });

    const response = await batchPutItem(
      UPDATE_BATCH_TRANSACTION_ORDERS,
      payload,
      "Transactions updated successfully!"
    );

    if (!response) {
      return;
    }

    setRows((prevRows) =>
      prevRows.map((row) => {
        const draft = editedRows[row.id];
        if (!draft) {
          return row;
        }

        if (row.isGroup) {
          return buildGroupRow({
            groupId: row.groupId,
            orders: row.orders.map((order) => ({
              ...order,
              ...draft,
              transactionStatus: draft.transactionStatus
                ? draft.transactionStatus
                : draft.paymentDate !== undefined
                ? draft.paymentDate ? "PAID" : order.transactionStatus
                : order.transactionStatus,
              supplierName:
                draft.supplierId !== undefined
                  ? supplierNameById[String(draft.supplierId)] || order.supplierName
                  : order.supplierName,
              bankName:
                draft.bankId !== undefined
                  ? bankNameById[String(draft.bankId)] || order.bankName
                  : order.bankName,
            })),
          });
        }

        const mergedRow = mergeRowWithDraft(row);
        return {
          ...row,
          ...mergedRow,
          transactionStatus: draft.transactionStatus
            ? mergedRow.transactionStatus
            : draft.paymentDate !== undefined
            ? mergedRow.paymentDate ? "PAID" : "PENDING"
            : mergedRow.transactionStatus,
          supplierName:
            supplierNameById[String(mergedRow.supplierId)] || row.supplierName,
          bankName: bankNameById[String(mergedRow.bankId)] || row.bankName,
        };
      })
    );
    setEditedRows({});
    setIsBatchEditMode(false);
  };

  const fetchPage = (queryOptions, filterModel) => {
    const storedFilter = sessionStorage.getItem("filterState");
    const parsedFilter = storedFilter ? JSON.parse(storedFilter) : null;
    let filterItems = filterModel.items
      ? [...filterModel.items]
      : parsedFilter?.items
      ? [...parsedFilter.items]
      : [];

    filterItems = filterItems.map((filterItem) => {
      if (filterItem && typeof filterItem.value === "string") {
        return { ...filterItem, value: filterItem.value.trim() };
      }
      return filterItem;
    });
    getPage(
      !isStarredPage
        ? GET_GROUPED_TRANSACTION_ORDERS
        : GET_STARRED_TRANSACTION_ORDERS,
      error_message,
      queryOptions,
      filterItems
    ).then((data) => {
      if (isStarredPage) {
        setRows(data.content);
      } else {
        // The backend returns one globally sorted page containing both row types.
        setRows(
          data.rows
            ? data.rows.map((row) =>
                row.group
                  ? buildGroupRow({ groupId: row.groupId, orders: row.orders })
                  : row.order
              )
            : [
                ...(data.groups || []).map(buildGroupRow),
                ...(data.ungroupedOrders || []),
              ]
        );
      }
      setRowCount(data.totalElements);
      setEditedRows({});
      setIsBatchEditMode(false);
    });
  };
  const closeStarPopup = () => setShowStarPopup(false);
  const openStarPopup = (OrderId) => {
    setSelectedId(OrderId);
    setShowStarPopup(true);
  };

  const confirmUnstarCallBack = () => {
    setRows((prevData) => prevData.filter((Order) => Order.id !== selectedId));
    closeStarPopup();
  };

  const confirmStarCallBack = () => {
    closeStarPopup();
  };

  const ConfirmStarPopUp = (OrderId) => {
    if (isStarredPage) {
      unstarOrder(
        UNSTAR_ORDERS_URL,
        { orderId: OrderId },
        confirmUnstarCallBack,
        `Order Unstarred Successfully!`,
        `Failed to Unstar Order!`
      );
    } else {
      starOrder(
        STAR_ORDERS_URL,
        { orderId: OrderId },
        confirmStarCallBack,
        `Order Starred Successfully!`,
        `Failed to Star Order!`
      );
    }
  };
  const meatballsMenuOptions = (order) =>
    order.isGroup
      ? [
          {
            label: "Edit Group Transactions",
            icon: <EditIcon />,
            color: "warning",
            action: () => openEditPopup(order),
            showOption: isEdit,
          },
          {
            label: "Preview Transaction Details",
            icon: <InfoRoundedIcon />,
            color: "info",
            action: () => {
              navigate(
                DefaultRoutes.transactionGroupDetails.replace(
                  ":groupId",
                  encryptId(order.groupId)
                )
              );
            },
            showOption: true,
          },
          {
            label: "Go To Order",
            icon: <ArrowRightAltIcon />,
            color: "warning",
            action: () => setSelectedGroup(order),
            showOption: true,
          },
        ]
      : singleOrderMenuOptions(order);

  const singleOrderMenuOptions = (order) => [
    {
      label: "Edit Transaction",
      icon: <EditIcon />,
      color: "warning",
      action: () => openEditPopup(order),
      showOption: isEdit,
    },
    {
      label: "Preview Transaction Details",
      icon: <InfoRoundedIcon />,
      color: "info",
      action: () => {
        navigate(
          DefaultRoutes.transactionDetails.replace(
            ":orderId",
            encryptId(order.id)
          )
        );
      },
      showOption: true,
    },
    {
      label: "Go To Order",
      icon: <ArrowRightAltIcon />,
      color: "warning",
      action: () => {
        navigate(
          DefaultRoutes.orderInfo.replace(":orderId", encryptId(order.id))
        );
      },
      showOption: true,
    },
    {
      label: "unStar Order",
      icon: <StarIcon color="red" />,
      color: "info",
      action: () => {
        openStarPopup(order.id);
      },
      showOption: isStarredPage,
    },
        {
      label: "Star Order",
      icon: <StarBorderIcon />,
      action: () => {
        openStarPopup(order.id);
      },
      showOption: !isStarredPage,
    },
  ];
  //********************* CANCEL POPUP *********************//
  const [showCancelPopup, setShowCancelPopup] = useState(false);

  const { loading: cancelOrderLoading, deleteItem: cancelOrder } =
    useDeleteItem();

  const closePopup = () => setShowCancelPopup(false);

  const openDeletePopup = (OrderId) => {
    setSelectedId(OrderId);
    setShowCancelPopup(true);
  };

  const confirmCancelCallBack = (canceledOrderId) => {
    setRows((prevData) =>
      prevData.filter((Order) => Order.id !== canceledOrderId)
    );
    closePopup();
  };

  const ConfirmCancel = (OrderId) => {
    cancelOrder(
      OrderId,
      confirmCancelCallBack,
      DELETE_ORDER,
      `Order Canceled Successfully!`,
      `Failed to Cancel Order!`
    );
  };
  const formatDate = (date) => {
    if (!date) return "N/A";

    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString("en-GB");
  };

  const formatState = (state) => {
    if (!state) return "Pending";
    return state
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const renderTextEditor = (params, field, options = {}) => (
    <TextField
      value={getDraftValue(params.row, field)}
      onChange={(event) =>
        updateEditedRow(params.row, field, event.target.value)
      }
      size="small"
      type={options.type || "text"}
      fullWidth
      variant="outlined"
      inputProps={options.inputProps}
      sx={{ backgroundColor: "#fff", minWidth: options.minWidth || 140 }}
    />
  );

  const renderSelectEditor = (params, field, options) => (
    <TextField
      select
      value={getDraftValue(params.row, field)}
      onChange={(event) =>
        updateEditedRow(params.row, field, event.target.value)
      }
      size="small"
      fullWidth
      variant="outlined"
      sx={{ backgroundColor: "#fff", minWidth: options.minWidth || 140 }}
    >
      {options.items.map((item) => (
        <MenuItem key={item.id} value={String(item.id)}>
          {item.name}
        </MenuItem>
      ))}
    </TextField>
  );

  const renderEditableCell = (params, field, editorRenderer, displayValue) => {
    if (isBatchEditMode) {
      return editorRenderer(params);
    }
    return displayValue;
  };

  const getSupplierDisplayName = (row) =>
    row?.supplierName || row?.supplier?.supplierName || "N/A";

  const getBankDisplayName = (row) =>
    row?.bankName || row?.bank?.bankName || "N/A";

  const getColumnSizing = (width, minWidth = width) =>
    isBatchEditMode
      ? {
          width,
          minWidth,
          flex: undefined,
        }
      : {
          flex: 2,
          minWidth,
        };

  const downloadCSV = (selectedColumns) => {
    closeExportPopup();
    // Set downloading state to true to show snackbar
    setIsDownloading(true);

    // Determine the export URL based on the order state
    let exportUrl = EXPORT_TRANSACTION_ORDERS_XSLS;
    let filterModel = JSON.parse(
      sessionStorage.getItem("_transaction-order-listing_filterState")
    );
    const payload = {
      filtersDto: filterModel?.items || [],
      selectedColumns,
    };
    return axiosInstance
      .post(exportUrl, payload, {
        responseType: "blob",
      })

      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.setAttribute("download", "orders.xlsx");
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
        // Set downloading state to false to hide snackbar
        setIsDownloading(false);
      })
      .catch((error) => {
        console.error("Excel download failed:", error);
        // Set downloading state to false in case of error
        setIsDownloading(false);
        setSnackbarMessage("Excel download failed");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  };
  //********************* Edit Popup *********************//
  const [showEditPopup, setShowEditPopup] = useState(false);
  const closeEditPopup = () => setShowEditPopup(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [sectionData, setSectionData] = useState({});
  const confirmEdit = async (newData) => {
    let updatedOrderData = {
      ...newData,
      orderId: Number(selectedId),
    };

    // Determine transaction status based on paymentDate presence
    const hasPaymentDate =
      updatedOrderData.paymentDate !== undefined &&
      updatedOrderData.paymentDate !== null &&
      updatedOrderData.paymentDate !== "";
    const currentRow = rows.find((row) => row.id === selectedId);
    if (currentRow?.isGroup) {
      const groupResponse = await putItem(
        UPDATE_GROUP_TRANSACTION_ORDER,
        { ...newData, groupId: currentRow.groupId },
        `Group Transactions Edited Successfully!`
      );
      if (groupResponse) {
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === selectedId
              ? buildGroupRow({
                  groupId: row.groupId,
                  orders: row.orders.map((order) => ({
                    ...order,
                    ...newData,
                    transactionStatus: deriveTransactionStatus(
                      newData,
                      order.transactionStatus
                    ),
                  })),
                })
              : row
          )
        );
        closeEditPopup();
      }
      return;
    }
    updatedOrderData.transactionStatus =
      normaliseDateValue(updatedOrderData.paymentDate) !==
      normaliseDateValue(currentRow?.paymentDate)
        ? hasPaymentDate ? "PAID" : "PENDING"
        : currentRow?.transactionStatus || (hasPaymentDate ? "PAID" : "PENDING");

    const response = await putItem(
      UPDATE_TRANSACTION_ORDER,
      updatedOrderData,
      `Order Edited Successfully!`
    );

    // Only update UI if API call succeeded
    if (response) {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === selectedId
            ? {
                ...row,
                // Merge updated fields; ensure transactionStatus reflects paymentDate
                ...updatedOrderData,
                transactionStatus: updatedOrderData.transactionStatus,
                // keep existing formatting/fields for paymentDate if not provided
                paymentDate:
                  updatedOrderData.paymentDate !== undefined
                    ? updatedOrderData.paymentDate
                    : row.paymentDate,
              }
            : row
        )
      );
      closeEditPopup();
    }
  };
  const FormFields = [
    {
      size: 4,
      type: inputTypes.FLOAT_NUMBER,
      key: "exchangeRate",
      label: "Exchange Rate",
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.TEXT,
      key: "transactionComments",
      label: "Transaction Comments",
      editable: true,
    },
    {
      size: 4,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "debitAccount",
      label: "Debit Account",
      options: DebitAccount,
      editable: true,
    },
    {
      size: 6,
      type: inputTypes.DATE,
      key: "authorizationDate",
      label: "Authorization Date",
      editable: true,
    },
    {
      size: 6,
      type: inputTypes.DATE,
      key: "paymentDate",
      label: "Payment Date",
      editable: true,
    },
    {
      size: 6,
      type: inputTypes.BOOLEAN,
      key: "submittedOnPortal",
      label: "Submitted on Portal",
      editable: true,
    },
  ];

  const openEditPopup = (rowData) => {
    if (!rowData) {
      return;
    }
    // Extract only editable fields from the selected row to seed the popup form
    const source = rowData.isGroup ? rowData.editValues : rowData;
    const editableSectionData = FormFields.filter(
      (field) => field.editable
    ).reduce((acc, field) => {
      acc[field.key] =
        field.type === inputTypes.BOOLEAN
          ? source[field.key] ?? false
          : source[field.key] !== undefined && source[field.key] !== null
          ? source[field.key]
          : "";
      return acc;
    }, {});

    setSectionData(editableSectionData);
    setSelectedId(rowData.id);
    setShowEditPopup(true);
  };

  //********************* INSERT DOCUMENT POPUP *********************//
  const [showInsertDocumentPopup, setShowInsertDocumentPopup] = useState(false);
  const { loading: insertDocumentLoading, postItem: insertDocument } =
    usePostItem();

  const stringCustomOperators = getGridStringOperators().filter((operator) =>
    [
      "contains",
      "doesNotContain",
      "equals",
      "doesNotEqual",
      "startsWith",
      "endsWith",
      "isEmpty",
      "isNotEmpty",
    ]?.includes(operator.value)
  );

  const numberCustomOperators = getGridNumericOperators().filter((operator) =>
    ["=", "!=", ">", ">=", "<", "<=", "isEmpty", "isNotEmpty"]?.includes(
      operator.value
    )
  );

  const renderGroupTransactionStatuses = (row) => {
    const statusColors = {
      PENDING: "orange",
      AUTHORIZED: theme.palette.purple.main,
      SUBMITTED: theme.palette.blue.main,
      PAID: "green",
    };

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {(row.groupStatuses || []).map((status) => (
          <span
            key={status}
            style={{
              color: statusColors[status] || "inherit",
              fontStyle: status === "PAID" ? "italic" : "normal",
              whiteSpace: "nowrap",
            }}
          >
            {"•"} {formatState(status)}
          </span>
        ))}
      </Box>
    );
  };

  const baseColumns = [
    {
      field: "poNumber",
      headerName: "PO Number",
      flex: 2,
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
    },
    {
      field: "transactionStatus",
      headerName: "Transaction Status",
      ...getColumnSizing(190, 170),
      type: "singleSelect",
      valueOptions: castOptionsToFitOptionsFilter(transactionStatus),
      filterOperators: GenericFilterForEnums(transactionStatus),
      renderCell: (params) => {
        if (isBatchEditMode) {
          return renderSelectEditor(params, "transactionStatus", {
            items: transactionStatus,
            minWidth: 120,
          });
        }
        const status = params.row.transactionStatus || "PENDING";
        const statusColors = {
          PENDING: "orange",
          AUTHORIZED: theme.palette.purple.main,
          SUBMITTED: theme.palette.blue.main,
          PAID: "green",
        };
        return (
          <span
            style={{
              color: statusColors[status] || "inherit",
              fontStyle: status === "PAID" ? "italic" : "normal",
              display: "flex",
              alignItems: "center",
            }}
          >
            {"•"} {formatState(status)}
          </span>
        );
      },
    },
    {
      field: "bolNumber",
      headerName: "BOL Number",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
    },
    {
      field: "bolDate",
      headerName: "BOL Date",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "date",
      valueFormatter: (params) => formatDate(params),
      hideable: true,
    },
    {
      field: "maturityType",
      headerName: "Maturity Type",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "singleSelect",
      valueOptions: castOptionsToFitOptionsFilter(MaturityType),
      filterOperators: GenericFilterForEnums(MaturityType),
      hideable: true,
    },
    {
      field: "maturityPeriod",
      headerName: "Maturity Period",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "number",
      filterOperators: numberCustomOperators,
      hideable: true,
    },
    {
      field: "maturityDate",
      headerName: "Maturity Date",
      sortable: true,
      filterable: true,
      ...getColumnSizing(190, 170),
      type: "date",
      valueFormatter: (params) => formatDate(params),
      hideable: true,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "maturityDate",
          (editableParams) =>
            renderTextEditor(editableParams, "maturityDate", {
              type: "date",
            }),
          formatDate(params.row.maturityDate)
        ),
    },
    {
      field: "supplier",
      headerName: "Supplier",
      ...getColumnSizing(240, 200),
      valueGetter: (_, row) => getSupplierDisplayName(row),
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "supplierId",
          (editableParams) =>
            renderSelectEditor(editableParams, "supplierId", {
              items: supplierOptions,
              minWidth: 180,
            }),
          supplierNameById[String(getDraftValue(params.row, "supplierId"))] ||
            getSupplierDisplayName(params.row)
        ),
    },
    {
      field: "bank",
      headerName: "Bank",
      sortable: true,
      filterable: true,
      ...getColumnSizing(240, 200),
      valueGetter: (_, row) => getBankDisplayName(row),
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "bankId",
          (editableParams) =>
            renderSelectEditor(editableParams, "bankId", {
              items: bankOptions,
              minWidth: 180,
            }),
          bankNameById[String(getDraftValue(params.row, "bankId"))] ||
            getBankDisplayName(params.row)
        ),
    },
    {
      field: "idcNumber",
      headerName: "IDC Number",
      sortable: true,
      filterable: true,
      ...getColumnSizing(180, 160),
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "idcNumber",
          (editableParams) => renderTextEditor(editableParams, "idcNumber"),
          params.row.idcNumber || "N/A"
        ),
    },
    {
      field: "invoiceAmount",
      headerName: "Invoice Amount",
      sortable: true,
      filterable: true,
      ...getColumnSizing(180, 160),
      type: "number",
      filterOperators: numberCustomOperators,
      hideable: true,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "invoiceAmount",
          (editableParams) =>
            renderTextEditor(editableParams, "invoiceAmount", {
              type: "number",
              inputProps: { step: "0.01" },
            }),
          params.row.invoiceAmount ?? "N/A"
        ),
    },
    {
      field: "proceededManually",
      headerName: "Proceeded Manually",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "boolean",
      hideable: true,
    },
     {
      field: "coraId",
      headerName: "CORA ID",
      sortable: true,
      filterable: true,
      flex: 0,
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
    },
    {
      field: "debitValue",
      headerName: "Debit Value",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "number",
      filterOperators: numberCustomOperators,
      hideable: true,
    },
     {
      field: "transactionComments",
      headerName: "Transaction Comments",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
    },
    {
      field: "invoiceDate",
      headerName: "Invoice Date",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "date",
      // valueGetter: (params) => params?.invoiceDate,
      valueFormatter: (params) => formatDate(params),

    },
    {
      field: "invoiceCurrency",
      headerName: "Invoice Currency",
      sortable: true,
      filterable: true,
      ...getColumnSizing(180, 150),
      type: "string",
      filterOperators: stringCustomOperators,
      hideable: true,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "invoiceCurrency",
          (editableParams) =>
            renderSelectEditor(editableParams, "invoiceCurrency", {
              items: Currency.filter((item) => item.id !== "Select Currency"),
              minWidth: 120,
            }),
          params.row.invoiceCurrency || "N/A"
        ),
    },

    {
      field: "paymentDate",
      headerName: "Payment Date",
      sortable: true,
      filterable: true,
      ...getColumnSizing(190, 170),
      type: "date",
      valueFormatter: (params) => formatDate(params),
      hideable: true,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "paymentDate",
          (editableParams) =>
            renderTextEditor(editableParams, "paymentDate", {
              type: "date",
            }),
          formatDate(params.row.paymentDate)
        ),
    },
    {
      field: "submittedOnPortal",
      headerName: "Submitted on Portal",
      ...getColumnSizing(190, 170),
      type: "boolean",
      hideable: true,
      valueGetter: (value) => value ?? false,
      renderCell: (params) => (params.row.submittedOnPortal ? "Yes" : "No"),
    },
    {
      field: "authorizationDate",
      headerName: "Authorization Date",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "date",
      valueFormatter: (params) => formatDate(params),
      hideable: true,
    },
    {
      field: "uploadOnPortalDate",
      headerName: "Upload On Portal Date",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "date",
      valueFormatter: (params) => formatDate(params),
      hideable: true,
    },
    {
      field: "form4SendingDate",
      headerName: "Form4 Sent",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "date",
      valueFormatter: (params) => formatDate(params),
      hideable: true,
    },
    {
      field: "form4ReceivalDate",
      headerName: "Form4 Received",
      sortable: true,
      filterable: true,
      flex: 2,
      type: "date",
      valueFormatter: (params) => formatDate(params),
      hideable: true,
    },
    {
      field: "debitAccount",
      headerName: "Debit Account",
      ...getColumnSizing(170, 150),
      type: "string",
      filterOperators: stringCustomOperators,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "debitAccount",
          (editableParams) =>
            renderSelectEditor(editableParams, "debitAccount", {
              items: DebitAccount,
              minWidth: 120,
            }),
          params.row.debitAccount || "N/A"
        ),
    },
    {
      field: "exchangeRate",
      headerName: "Exchange Rate",
      sortable: true,
      filterable: true,
      ...getColumnSizing(180, 160),
      type: "number",
      filterOperators: numberCustomOperators,
      hideable: true,
      renderCell: (params) =>
        renderEditableCell(
          params,
          "exchangeRate",
          (editableParams) =>
            renderTextEditor(editableParams, "exchangeRate", {
              type: "number",
              inputProps: { step: "0.000001" },
            }),
          params.row.exchangeRate ?? "N/A"
        ),

    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      ...(isBatchEditMode
        ? {
            width: 120,
            minWidth: 120,
            flex: undefined,
          }
        : {
            flex: 1.5,
            minWidth: 120,
          }),
      renderCell: (params) => (
        <ThemeProvider theme={theme}>
          {!isBatchEditMode && (
            <Tooltip title="Actions">
              <MeatballsMenu options={meatballsMenuOptions(params.row)} />
            </Tooltip>
          )}
        </ThemeProvider>
      ),
    },
  ];

  // Group rows show the summary computed in buildGroupRow instead of the editable cells.
  const columns = baseColumns.map((column) =>
    column.field === "actions"
      ? column
      : {
          ...column,
          renderCell: (params) =>
            params.row.isGroup &&
            column.field === "transactionStatus" &&
            !isBatchEditMode
              ? renderGroupTransactionStatuses(params.row)
              : params.row.isGroup &&
                !(isBatchEditMode && BATCH_EDITABLE_FIELDS.has(column.field))
              ? params.row.groupDisplay?.[column.field] ?? ""
              : column.renderCell
              ? column.renderCell(params)
              : params.formattedValue,
        }
  );

  const initialColumnVisibilityModel = {
    supplier: true,
    bank: true,
    invoiceAmount: true,
    maturityDate: true,
    exchangeRate: false,
    debitAccount: false,
    idcNumber: false,
    invoiceCurrency: false,
    paymentDate: false,
    submittedOnPortal: false,
    idcNumber: false,
    uploadOnPortalDate: false,
    proceededManually: false,
    coraId: false,
    authorizationDate: false,
    bolNumber: false,
    bolDate: false,
    maturityType: false,
    maturityPeriod: false,
    invoiceDate: false,
    invoiceNumber: false,
    form4SendingDate: false,
    form4ReceivalDate: false,
    transactionComments: false,
    debitValue: false,
    invoiceNumber: false,
    invoiceAmount: false,
  };

  const PaymentsTableProps = {
    columns,
    disableRowSelectionOnClick: true,
    disableColumnMenu: true,
    initialColumnVisibilityModel,
    rowCount,
    rows,
    isLoading: pageLoading,
    fetchPage,
    exportUrl: EXPORT_PAYMENT_XSLS,
    exportOnClick: openExportPopup,
    sortBy: "transactionStatus",
    enableHorizontalScroll: isBatchEditMode,
  };

  const batchEditButtons =
    isEdit &&
    !isStarredPage &&
    !showEditPopup && [
      !isBatchEditMode ? (
        <Button
          key="edit-current-page"
          variant="contained"
          color="primary"
          onClick={openBatchEditMode}
          disabled={
            primitiveLoading ||
            pageLoading ||
            rows.length === 0
          }
        >
          Edit Current Page
        </Button>
      ) : (
        <Button
          key="discard-batch"
          variant="outlined"
          color="inherit"
          onClick={discardBatchChanges}
          disabled={batchPutLoading}
        >
          Discard All
        </Button>
      ),
      isBatchEditMode ? (
        <Button
          key="save-batch"
          variant="contained"
          onClick={batchSaveCurrentPage}
          disabled={!hasUnsavedBatchEdits || batchPutLoading}
        >
          Save All
        </Button>
      ) : null,
    ].filter(Boolean);

  const handleAddTransactionButton = () => {
    navigate(DefaultRoutes.invoice);
  };

  //********************* RETURN *********************//
  return (
    <Box>
      <ListingPageTemplate
        title={title}
        PageIcon={<AccountBalanceIcon color="white" fontSize="large" />}
        iconColor={theme.palette.primary.main}
        buttons={batchEditButtons}
        tableProps={PaymentsTableProps}
        serverPagination={true}
      />
      <ExportSelectionPopover
        open={isExportPopoverOpen}
        anchorPosition={exportAnchorPosition}
        onClose={closeExportPopup}
        onExport={downloadCSV}
        columnOptions={TRANSACTION_EXPORT_COLUMN_OPTIONS}
        description="Choose which transaction columns to include in the Excel export."
      />
      {EditSectionPopup(
        "Transaction",
        showEditPopup,
        closeEditPopup,
        confirmEdit,
        PutItemLoading,
        FormFields,
        sectionData,
        false,
        isPageLoading
      )}
      <GenericPopup
        open={Boolean(selectedGroup)}
        title={`Group Orders (${selectedGroup?.orders?.length || 0})`}
        icon={<VisibilityRoundedIcon color="white" fontSize="large" />}
        maxWidth="sm"
        content={
          selectedGroup?.orders?.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {selectedGroup.orders.map((order) => (
                <Box
                  key={order.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    p: "10px 12px",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={600} noWrap>
                      {order.poNumber || `Order ${order.id}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {joinValues([
                        `ID: ${order.id}`,
                        order.supplierName || order.supplier?.supplierName,
                      ])}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    endIcon={<ArrowRightAltIcon />}
                    onClick={() =>
                      navigate(
                        DefaultRoutes.orderInfo.replace(
                          ":orderId",
                          encryptId(order.id)
                        )
                      )
                    }
                  >
                    Details
                  </Button>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No orders found in this group.
            </Typography>
          )
        }
        buttons={[
          {
            text: "Close",
            variant: "outlined",
            color: "primary",
            onClick: () => setSelectedGroup(null),
          },
        ]}
      />
      {DeletePopup(
        "Order",
        showStarPopup,
        closeStarPopup,
        ConfirmStarPopUp,
        selectedId,
        isStarredPage ? unstarOrderLoading : starOrderLoading,
        isStarredPage ? "Unstar" : "Star"
      )}
      <Snackbar
        open={isDownloading || openSnackbar}
        autoHideDuration={openSnackbar ? 6000 : null}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={isDownloading ? "info" : snackbarSeverity}
          onClose={() => (isDownloading ? null : setOpenSnackbar(false))}
        >
          {isDownloading ? "Downloading Excel..." : snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TreasuryCoordinatorOrderListing;
